import { useMemo, useState } from 'react';
import { CREATOR_KINDS } from '../../lib/welcome';
import { useCountFromZero, useInView, useTicker } from '../../lib/motion';

interface CreatorPoolProps {
  memberNo: number | null;
}

const COLS = 26;
const ROWS = 9;

/**
 * The pool, drawn as a field of lights with yours in it. Every other light is
 * someone else waiting — hover one and it says what kind of creator.
 */
export default function CreatorPool({ memberNo }: CreatorPoolProps) {
  const [ref, seen] = useInView<HTMLDivElement>(0.3);
  const shown = useCountFromZero(memberNo ?? 0, seen && memberNo !== null, 1600);
  const spotlight = useTicker(CREATOR_KINDS.length, 1300, seen);
  const [hover, setHover] = useState<number | null>(null);

  const total = COLS * ROWS;
  const you = memberNo ? (memberNo - 1) % total : Math.floor(total / 2);

  const dots = useMemo(() => {
    let state = 1337;
    const rand = () => {
      state = (state * 16807) % 2147483647;
      return state / 2147483647;
    };
    return Array.from({ length: total }, (_, i) => ({
      i,
      x: (i % COLS) * 20 + 10,
      y: Math.floor(i / COLS) * 20 + 10,
      kind: CREATOR_KINDS[Math.floor(rand() * CREATOR_KINDS.length)],
      delay: Math.round(rand() * 900 + ((i % COLS) / COLS) * 700),
      twinkle: rand() < 0.22
    }));
  }, [total]);

  const hovered = hover === null ? null : dots[hover];

  return (
    <div ref={ref} className={`pool${seen ? ' in' : ''}`}>
      <div className="pool-head">
        <p className="mono">Early Access pool</p>
        <h2 className="d pool-title">
          {memberNo ? (
            <>
              You&rsquo;re creator <span className="pool-no">#{shown}</span>
              <br />
              in the pool.
            </>
          ) : (
            <>
              You&rsquo;re in
              <br />
              the pool.
            </>
          )}
        </h2>
        {memberNo ? (
          <p className="sub">
            <b>{memberNo.toLocaleString()} creators and counting.</b>
          </p>
        ) : null}
      </div>

      <div className="pool-field">
        <svg viewBox={`0 0 ${COLS * 20} ${ROWS * 20}`} onPointerLeave={() => setHover(null)} role="img" aria-label="Creators in the Early Access pool, with you highlighted">
          {dots.map(dot =>
            dot.i === you ? null : (
              <circle
                key={dot.i}
                cx={dot.x}
                cy={dot.y}
                r={hover === dot.i ? 5 : 2.6}
                className={`pdot${dot.twinkle ? ' tw' : ''}${hover === dot.i ? ' hot' : ''}`}
                style={{ '--d': `${dot.delay}ms` } as React.CSSProperties}
                onPointerEnter={() => setHover(dot.i)}
              />
            )
          )}
          <g className="pdot-you" transform={`translate(${dots[you].x} ${dots[you].y})`} onPointerEnter={() => setHover(you)}>
            <circle r="12" className="pdot-ring" />
            <circle r="5" className="pdot-core" />
          </g>
        </svg>
        <div className="pool-tip mono" aria-live="polite">
          {hovered ? (hovered.i === you ? 'That’s you' : hovered.kind) : 'Hover the lights'}
        </div>
      </div>

      <ul className="kinds" aria-label="Who is in the pool">
        {CREATOR_KINDS.map((kind, index) => (
          <li key={kind} className={index === spotlight ? 'lit' : ''}>
            {kind}.
          </li>
        ))}
      </ul>
      <p className="sub pool-close">
        All waiting to see what happens when AI becomes part of an actual production workflow — not just another
        generation tool.
      </p>
    </div>
  );
}
